/**
 * Sales management page — multi-branch, multi-payment-mode sales with
 * inline edit, bulk-VAT, duplicate detection, and POS-by-branch
 * grouping inside each daily summary.
 *
 * iter733 — Refactored from a 1,667-line monolith into a thin
 * orchestrator that delegates UI chunks to focused components under
 * /components/sales. State, API calls, and handler logic still live
 * here so the data flow stays in one place. All data-testid
 * attributes preserved verbatim across the move.
 */
import { CheckExportBtn } from '@/components/CheckExportBtn';
import { handleExport, REPORT_EXPORT_COLS } from '@/lib/exportCsv';
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Plus, ShoppingCart, HelpCircle, FileDown, MoreHorizontal, Download, Scale, SlidersHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cur } from '@/lib/currency';
import { PageSizeSelector } from '@/components/common/PageSizeSelector';
import { exportInvoicesMergedPDF, invoicePdfBase64 } from '@/lib/pdfExport';
import api from '@/lib/api';
import { getCompanySettings } from '@/lib/configCache';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ExportButtons } from '@/components/ExportButtons';
import { AdvancedSearch } from '@/components/AdvancedSearch';
import { useBranchStore } from '@/stores';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { localToday, localDateTimeStamp } from '@/lib/dateRange';
import { FilterEmptyNudge } from '@/components/common/FilterEmptyNudge';
import { PDFExportButton } from '@/components/PDFExportButton';
import SalesVsBankWidget from '@/components/sales/SalesVsBankWidget';
import { BranchSummaryCard } from '@/components/sales/BranchSummaryCard';
import { SaleFormCard } from '@/components/sales/SaleFormCard';
import { BulkVatToolbar } from '@/components/sales/BulkVatToolbar';
import { SalesGrandTotalBar } from '@/components/sales/SalesGrandTotalBar';
import { SalesDailyTable } from '@/components/sales/SalesDailyTable';
import { SalesFlatTable } from '@/components/sales/SalesFlatTable';
import { SaleJournalDialog } from '@/components/sales/SaleJournalDialog';
import { ReceiveCreditDialog, DuplicateWarningDialog } from '@/components/sales/SalesDialogs';
import { FinancialSummaryHeader } from '@/components/FinancialSummaryHeader';
import { ReportHeader } from '@/components/reports/ReportUI';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { SalesReconciliationDialog } from '@/components/sales/SalesReconciliationDialog';
import { useAuthStore } from '@/stores/authStore';
import { useRealtimeRefresh } from '@/hooks/useRealtimeRefresh';

export default function SalesPage() {
  const { hasPermission } = useAuthStore();
  const canReconcile = hasPermission('sales');
  const [sales, setSales] = useState([]);
  const [branchSel, setBranchSel] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const [customers, setCustomers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [receivingSale, setReceivingSale] = useState(null);
  const [journalSaleId, setJournalSaleId] = useState(null);
  const [activeTab, setActiveTab] = useState('branch');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusSel, setStatusSel] = useState('');
  const [zatcaSel, setZatcaSel] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [dateRange, setDateRange] = useState(null);
  const [expandedSalesDates, setExpandedSalesDates] = useState({});
  const [groupByName, setGroupByName] = useState(false);
  // Daily-grouped view is now optional; the default is a flat transaction list
  // so 10/25/50 page sizes map 1:1 to transaction rows.
  const [dailyView, setDailyView] = useState(false);
  // iter731 — Per-(day, branch) expanded flags inside POS grouping.
  const [posBranchExpanded, setPosBranchExpanded] = useState({});

  // iter732 — Per-day CSV export.
  const exportDayCsv = (day) => {
    const rows = [['Date', 'Branch', 'Bill #', 'Type', 'Total', 'Cash', 'Bank', 'Online', 'Credit', 'Customer', 'Notes']];
    (day.items || []).forEach(s => {
      const pm = (s.payment_details || []).reduce((m, p) => ({ ...m, [p.mode]: (m[p.mode] || 0) + (p.amount || 0) }), {});
      rows.push([
        (s.date || '').slice(0, 19),
        (branches.find(b => b.id === s.branch_id)?.name || s.branch_id || ''),
        s.bill_number || s.order_number || s.check_number || s.external_reference || s.id.slice(0, 8),
        s.sale_type || '',
        (s.final_amount != null ? s.final_amount : (s.amount || 0)).toFixed(2),
        (pm.cash || 0).toFixed(2),
        (pm.bank || pm.card || 0).toFixed(2),
        (pm.online || pm.online_platform || 0).toFixed(2),
        (pm.credit || 0).toFixed(2),
        (s.customer_name || '').replace(/[",\n]/g, ' '),
        (s.notes || '').replace(/[",\n]/g, ' '),
      ]);
    });
    const csv = rows.map(r => r.map(c => /[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-${day.dateKey}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  const [serverBranchBreakdown, setServerBranchBreakdown] = useState({});
  const [serverTotalAmount, setServerTotalAmount] = useState(0);
  const [serverCashTotal, setServerCashTotal] = useState(0);
  const [serverBankTotal, setServerBankTotal] = useState(0);
  const [serverCreditTotal, setServerCreditTotal] = useState(0);
  const [serverOnlineTotal, setServerOnlineTotal] = useState(0);
  const [serverReceivableTotal, setServerReceivableTotal] = useState(0);
  const [serverReceivedTotal, setServerReceivedTotal] = useState(0);
  const [serverPlatformPending, setServerPlatformPending] = useState(0);
  const [serverVat, setServerVat] = useState(0);

  // iter395m — Gross/Net display toggle (persisted).
  const [amountView, setAmountView] = useState(() => {
    try { return localStorage.getItem('sales-amount-view') || 'net'; } catch { return 'net'; }
  });
  const handleAmountViewChange = (v) => {
    setAmountView(v);
    try { localStorage.setItem('sales-amount-view', v); } catch { /* ignore */ }
  };

  // iter403 — Cards vs Table view for per-branch panel (persisted).
  const [branchView, setBranchView] = useState(() => {
    try { return localStorage.getItem('sales-branch-view') || 'table'; } catch { return 'table'; }
  });
  const handleBranchViewChange = (v) => {
    setBranchView(v);
    try { localStorage.setItem('sales-branch-view', v); } catch { /* ignore */ }
  };

  // iter406 — Inline-edit a single field on a sale row.
  const inlineUpdateSale = async (saleId, patch) => {
    try {
      await api.put(`/sales/${saleId}`, patch);
      toast.success('Saved');
      fetchData(currentPage);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
      throw err;
    }
  };

  const [formData, setFormData] = useState({
    sale_type: 'branch',
    branch_id: '',
    customer_id: '',
    platform_id: '',
    payment_details: [{ mode: 'cash', amount: '' }],
    discount: '',
    date: localToday(),
    notes: '',
  });

  const [receivePayment, setReceivePayment] = useState({ payment_mode: 'cash', amount: '' });
  const [searchFilters, setSearchFilters] = useState({});
  const [bulkVatMode, setBulkVatMode] = useState(false);
  const [selectedSaleIds, setSelectedSaleIds] = useState(new Set());
  const [bulkVatRate, setBulkVatRate] = useState(15);
  const [bulkVatInclusive, setBulkVatInclusive] = useState(true);
  const [bulkSkipExisting, setBulkSkipExisting] = useState(true);
  const [bulkFilterBranch, setBulkFilterBranch] = useState('');
  const [bulkFilterStart, setBulkFilterStart] = useState('');
  const [bulkFilterEnd, setBulkFilterEnd] = useState('');
  // iter944 — Hide/Show summary header for more table space (persisted)
  const [showHeader, setShowHeader] = useState(() => {
    try { return localStorage.getItem('sales_show_header') !== '0'; } catch { return true; }
  });
  const toggleHeader = () => setShowHeader((v) => { const n = !v; try { localStorage.setItem('sales_show_header', n ? '1' : '0'); } catch {} return n; });
  // iter H4.2C — ZATCA filter is an advanced control; users can hide it via View ▾ (reuses the same localStorage pattern as showHeader).
  const [showZatca, setShowZatca] = useState(() => {
    try { return localStorage.getItem('sales_show_zatca') !== '0'; } catch { return true; }
  });
  const toggleZatca = () => setShowZatca((v) => { const n = !v; try { localStorage.setItem('sales_show_zatca', n ? '1' : '0'); } catch {} return n; });
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStart = searchParams.get('start_date');
  const urlEnd = searchParams.get('end_date');
  const urlBranch = searchParams.get('branch');
  const urlDateFilter = searchParams.get('date') || (urlStart && urlEnd ? `${urlStart} → ${urlEnd}` : null);
  const urlFocus = searchParams.get('focus');
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [duplicateExisting, setDuplicateExisting] = useState([]);
  const [includePivot, setIncludePivot] = useState(false);

  // iter915 — invoice-linked Email + Merge Selected to PDF (audit bundle)
  const [invoiceBySale, setInvoiceBySale] = useState({});
  const [companySettings, setCompanySettings] = useState({});
  const [pdfSelectedIds, setPdfSelectedIds] = useState([]);
  const [emailingSaleId, setEmailingSaleId] = useState(null);
  const [mergingPdf, setMergingPdf] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [iRes, coRes] = await Promise.all([
          api.get('/invoices?limit=1000'),
          getCompanySettings().then(d => ({ data: d })).catch(() => ({ data: {} })),
        ]);
        const map = {};
        const invList = iRes.data?.data || (Array.isArray(iRes.data) ? iRes.data : []);
        invList.forEach((inv) => { if (inv.sale_id) map[inv.sale_id] = inv; });
        setInvoiceBySale(map);
        setCompanySettings(coRes.data || {});
      } catch { /* invoices unavailable — invoice buttons stay hidden */ }
    })();
  }, []);

  const fetchQrForInvoice = async (invId) => {
    try { const { data } = await api.get(`/invoices/${invId}/zatca-qr`); return data.qr_data; } catch { return null; }
  };

  const handleEmailInvoiceForSale = async (sale) => {
    const inv = invoiceBySale[sale.id];
    if (!inv) { toast.info('No invoice linked to this sale'); return; }
    setEmailingSaleId(sale.id);
    try {
      const qr = await fetchQrForInvoice(inv.id);
      const pdf_base64 = await invoicePdfBase64(inv, companySettings, qr, cur());
      const { data } = await api.post(`/invoices/${inv.id}/email`, { pdf_base64, filename: `Invoice_${inv.invoice_number || inv.id}.pdf` });
      toast.success(`Invoice emailed to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to email invoice');
    } finally { setEmailingSaleId(null); }
  };

  const togglePdfSelect = (saleId) => setPdfSelectedIds((p) => (p.includes(saleId) ? p.filter((x) => x !== saleId) : [...p, saleId]));

  const handleMergeSelectedPdf = async () => {
    const invs = pdfSelectedIds.map((sid) => invoiceBySale[sid]).filter(Boolean);
    if (!invs.length) { toast.info('Select invoice-linked sales first'); return; }
    setMergingPdf(true);
    try {
      const qrMap = {};
      await Promise.all(invs.map(async (i) => { qrMap[i.id] = await fetchQrForInvoice(i.id); }));
      await exportInvoicesMergedPDF(invs, companySettings, qrMap, cur());
    } finally { setMergingPdf(false); }
  };

  useEffect(() => {
    // Deep link: ?date=YYYY-MM-DD or ?start_date&end_date[&branch] (e.g. from Branch Summary).
    const d = searchParams.get('date');
    if (d || (urlStart && urlEnd) || urlBranch) {
      setSearchFilters(prev => ({ ...prev, ...(d || urlStart ? { date: { start: d || urlStart, end: d || urlEnd } } : {}),
        ...(urlBranch ? { branch_id: urlBranch } : {}) }));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter431 — Focus / pulse-highlight a sale row when ?focus=<id>.
  useEffect(() => {
    if (!urlFocus || !sales?.length) return;
    const target = sales.find(s => s.id === urlFocus);
    if (!target) return;
    const dateKey = target.date ? format(new Date(target.date), 'yyyy-MM-dd') : 'unknown';
    setExpandedSalesDates(prev => ({ ...prev, [dateKey]: true }));
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="sale-detail-${urlFocus}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
        el.classList.add('row-focus-pulse');
        setTimeout(() => {
          el.classList.remove('row-focus-pulse');
          const sp = new URLSearchParams(searchParams);
          sp.delete('focus');
          setSearchParams(sp, { replace: true });
        }, 2800);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [urlFocus, sales]);

  const branchMonthlySales = useMemo(() => {
    const effectiveStart = searchFilters?.date?.start || dateRange?.start;
    const effectiveEnd = searchFilters?.date?.end || dateRange?.end;
    let label = format(new Date(), 'MMMM yyyy');
    if (effectiveStart && effectiveEnd) {
      label = effectiveStart === effectiveEnd ? effectiveStart : `${effectiveStart} to ${effectiveEnd}`;
    } else if (effectiveStart) {
      label = `From ${effectiveStart}`;
    }

    // Server breakdown = the COMPLETE filtered set (never just the current page), net or gross.
    if (serverBranchBreakdown && Object.keys(serverBranchBreakdown).length > 0) {
      const byBranch = {};
      let totalOnline = 0, totalAll = 0;
      Object.entries(serverBranchBreakdown).forEach(([name, data]) => {
        const t = amountView === 'gross' ? (data.gross ?? data.total ?? 0) : (data.total || 0);
        byBranch[name] = { cash: data.cash || 0, bank: data.bank || 0, credit: data.credit || 0, online: data.online || 0, total: t, count: data.count || 0 };
        totalOnline += data.online || 0; totalAll += t;
      });
      return { byBranch, totalOnline, totalAll: amountView === 'gross' ? totalAll : serverTotalAmount, month: label };
    }

    // Fallback / GROSS mode: compute from paginated rows.
    const byBranch = {};
    let totalOnline = 0;
    let totalAll = 0;
    sales.forEach(sale => {
      const branchId = sale.branch_id;
      const branch = branches.find(b => b.id === branchId);
      const branchName = branch?.name || 'Unknown';
      if (!byBranch[branchName]) byBranch[branchName] = { cash: 0, bank: 0, credit: 0, online: 0, total: 0, count: 0, branch_id: branch?.id || null };
      byBranch[branchName].count += 1;
      (sale.payment_details || []).forEach(p => {
        const amt = p.amount || 0;
        if (p.mode === 'cash') byBranch[branchName].cash += amt;
        else if (p.mode === 'bank') byBranch[branchName].bank += amt;
        else if (p.mode === 'credit') byBranch[branchName].credit += amt;
        else if (p.mode === 'online_platform' || p.mode === 'online') {
          byBranch[branchName].online += amt;
          totalOnline += amt;
        }
      });
      const gross = sale.amount || 0;
      const net = sale.final_amount != null ? sale.final_amount : (gross - (sale.discount || 0));
      const v = amountView === 'gross' ? gross : net;
      byBranch[branchName].total += v;
      totalAll += v;
    });
    return { byBranch, totalOnline, totalAll, month: label };
  }, [sales, branches, dateRange, searchFilters, serverBranchBreakdown, serverTotalAmount, amountView]);

  useEffect(() => {
    setCurrentPage(1);
    fetchData(1);
  }, [dateRange, searchFilters, branchSel, statusSel, pageSize, groupByName, dailyView]);

  // iter936 — "Group by Name" optional view: regroups the current page of
  // sales by counterparty (customer / platform / Walk-in) with count + total.
  const salesByName = useMemo(() => {
    const m = {};
    (sales || []).forEach((s) => {
      const name = s.customer_name || s.platform_name || 'Walk-in';
      if (!m[name]) m[name] = { name, count: 0, total: 0 };
      const gross = s.amount || 0;
      const net = s.final_amount != null ? s.final_amount : (gross - (s.discount || 0));
      m[name].count += 1;
      m[name].total += amountView === 'gross' ? gross : net;
    });
    return Object.values(m).sort((a, b) => b.total - a.total);
  }, [sales, amountView]);

  // "Group by Name" aggregates across the WHOLE filtered window, so it still
  // needs the bulk-fetched `sales` array and stays client-paginated by group.
  // The default day view is now paginated server-side (see fetchData), so
  // `sales` already IS the current page of rows — no re-slicing needed.
  const nameTotalPages = Math.max(1, Math.ceil(salesByName.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pagedSales = sales;
  const pagedNameGroups = useMemo(
    () => salesByName.slice(pageStart, pageStart + pageSize),
    [salesByName, pageStart, pageSize],
  );
  const effectiveTotalPages = groupByName ? nameTotalPages : totalPages;
  const goToPage = (p) => { if (groupByName) setCurrentPage(p); else fetchData(p); };

  const fetchData = async (page = 1) => {
    try {
      fetchBranches();
      // "Group by Name" needs the whole filtered window to aggregate correctly
      // (client-side, see salesByName below); the default day view now uses
      // true server-side row pagination — page/limit map 1:1 to rows shown.
      let salesUrl = (groupByName || dailyView) ? `/sales?page=1&limit=2000` : `/sales?page=${page}&limit=${pageSize}`;
      const effectiveStart = searchFilters?.date?.start || dateRange?.start;
      const effectiveEnd = searchFilters?.date?.end || dateRange?.end;
      if (effectiveStart) salesUrl += `&start_date=${effectiveStart}`;
      if (effectiveEnd) salesUrl += `&end_date=${effectiveEnd}`;
      if (searchFilters?.branch_id) salesUrl += `&branch_id=${searchFilters.branch_id}`;
      if (branchSel.length) salesUrl += `&branch_ids=${branchSel.join(',')}`;
      if (searchFilters?.payment_mode) salesUrl += `&payment_mode=${searchFilters.payment_mode}`;
      if (statusSel) salesUrl += `&payment_status=${statusSel}`;
      if (searchFilters?.final_amount?.min) salesUrl += `&amount_min=${searchFilters.final_amount.min}`;
      if (searchFilters?.final_amount?.max) salesUrl += `&amount_max=${searchFilters.final_amount.max}`;
      if (searchFilters?.search) salesUrl += `&search=${encodeURIComponent(searchFilters.search)}`;
      const [salesRes, customersRes, platformsRes] = await Promise.allSettled([
        api.get(salesUrl),
        api.get('/customers'),
        api.get('/platforms'),
      ]);
      if (salesRes.status === 'fulfilled') {
        const salesData = salesRes.value.data;
        setSales(salesData.data || salesData || []);
        setTotalRecords(salesData.total || (salesData.data || []).length);
        setServerBranchBreakdown(salesData.branch_breakdown || {});
        setServerTotalAmount(salesData.total_amount || 0);
        setServerCashTotal(salesData.cash_total || 0);
        setServerBankTotal(salesData.bank_total || 0);
        setServerCreditTotal(salesData.credit_total || 0);
        setServerOnlineTotal(salesData.online_total || 0);
        setServerReceivableTotal(salesData.receivable_total || 0);
        setServerReceivedTotal(salesData.received_total || 0);
        setServerPlatformPending(salesData.platform_pending_total || 0);
        setServerVat(salesData.vat_total || 0);
        if (!groupByName && !dailyView) {
          setCurrentPage(salesData.page || page);
          setTotalPages(salesData.pages || 1);
        }
      }
      if (salesRes.status !== 'fulfilled') {
        toast.error('Failed to load sales data');
      }
      if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data?.data || customersRes.value.data || []);
      if (platformsRes.status === 'fulfilled') setPlatforms(platformsRes.value.data || []);
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // H3 — realtime refresh: refetch the current filtered page when a sale
  // changes anywhere (POS, manual, edit, delete). Preserves branch/date/
  // search/pageSize/page because we re-run the SAME fetchData(currentPage).
  useRealtimeRefresh(['new_sale', 'sale_updated', 'sale_deleted'], () => fetchData(currentPage));

  const addPaymentRow = () => setFormData({ ...formData, payment_details: [...formData.payment_details, { mode: 'cash', amount: '' }] });
  const removePaymentRow = (index) => setFormData({ ...formData, payment_details: formData.payment_details.filter((_, i) => i !== index) });
  const updatePaymentRow = (index, field, value) => {
    const newPayments = [...formData.payment_details];
    newPayments[index][field] = value;
    setFormData({ ...formData, payment_details: newPayments });
  };

  const calculateTotals = () => {
    let cash = 0, bank = 0, credit = 0, online = 0;
    formData.payment_details.forEach(p => {
      const amount = parseFloat(p.amount) || 0;
      if (p.mode === 'cash') cash += amount;
      else if (p.mode === 'bank') bank += amount;
      else if (p.mode === 'credit') credit += amount;
      else if (p.mode === 'online_platform') online += amount;
    });
    const subtotal = cash + bank + credit + online;
    const discount = parseFloat(formData.discount) || 0;
    const total = subtotal - discount;
    return { cash, bank, credit, online, subtotal, discount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const t = calculateTotals();
    if (t.subtotal === 0) { toast.error('Please add at least one payment entry'); return; }
    const hasOnlinePayment = formData.payment_details.some(p => p.mode === 'online_platform' && parseFloat(p.amount) > 0);
    if (hasOnlinePayment && !formData.platform_id) { toast.error('Please select a delivery platform for online sales'); return; }
    // Duplicate check (scoped by customer).
    if (formData.branch_id && t.total > 0) {
      try {
        const cid = formData.customer_id || '';
        const url = `/sales/check-duplicate?branch_id=${encodeURIComponent(formData.branch_id)}&amount=${t.total}&date=${encodeURIComponent(formData.date)}${cid ? `&customer_id=${encodeURIComponent(cid)}` : ''}`;
        const checkRes = await api.get(url);
        if (checkRes.data?.has_duplicate) {
          setDuplicateCount(checkRes.data.count);
          setDuplicateExisting(checkRes.data.existing || []);
          setShowDuplicateWarning(true);
          return;
        }
      } catch { /* proceed if check fails */ }
    }
    await submitSale();
  };

  const submitSale = async () => {
    const t = calculateTotals();
    const hasOnlinePayment = formData.payment_details.some(p => p.mode === 'online_platform' && parseFloat(p.amount) > 0);
    try {
      const payload = {
        ...formData,
        amount: t.subtotal,
        discount: t.discount,
        payment_mode: hasOnlinePayment ? 'online_platform' : (t.credit > 0 ? 'credit' : (t.bank > 0 ? 'bank' : 'cash')),
        platform_status: hasOnlinePayment ? 'pending' : undefined,
        payment_details: formData.payment_details
          .filter(p => parseFloat(p.amount) > 0)
          .map(p => ({ mode: p.mode, amount: parseFloat(p.amount) })),
        date: localDateTimeStamp(formData.date),
      };
      await api.post('/sales', payload);
      toast.success('Sale added successfully');
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sale');
    }
  };

  const resetForm = () => setFormData({
    sale_type: activeTab,
    branch_id: '', customer_id: '', platform_id: '',
    payment_details: [{ mode: 'cash', amount: '' }],
    discount: '', date: localToday(), notes: '',
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale? If it is posted to the ledger it will be cancelled with a linked journal reversal (the original is kept in Cancelled Transactions).')) return;
    try {
      const _r = await api.delete(`/sales/${id}`);
      toast.success(_r.data?.message || 'Sale deleted successfully');
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (e?.response?.status === 400 && /reason is required/i.test(detail)) {
        const reason = window.prompt('This sale is posted to the ledger. Enter the cancellation reason (saved in the audit trail):');
        if (!reason || !reason.trim()) return;
        try {
          const _r2 = await api.delete(`/sales/${id}`, { params: { reason: reason.trim() } });
          toast.success(_r2.data?.message || 'Sale cancelled and journal reversed.');
          fetchData();
        } catch (e2) { toast.error(e2?.response?.data?.detail || 'Failed to cancel sale'); }
        return;
      }
      toast.error(detail || 'Failed to delete sale');
    }
  };

  const selectAllSales = () => {
    const all = sales.map(s => s.id);
    setSelectedSaleIds(prev => prev.size === all.length ? new Set() : new Set(all));
  };

  const applyBulkVatSales = async () => {
    const hasSelection = selectedSaleIds.size > 0;
    const hasFilters = bulkFilterBranch || bulkFilterStart || bulkFilterEnd;
    if (!hasSelection && !hasFilters) { toast.error('Select sales or set filters'); return; }
    try {
      const payload = { sale_ids: hasSelection ? Array.from(selectedSaleIds) : [], tax_rate: bulkVatRate, vat_inclusive: bulkVatInclusive, skip_existing_vat: bulkSkipExisting };
      if (!hasSelection && hasFilters) {
        if (bulkFilterBranch) payload.filter_branch_id = bulkFilterBranch;
        if (bulkFilterStart) payload.filter_start_date = bulkFilterStart;
        if (bulkFilterEnd) payload.filter_end_date = bulkFilterEnd;
      }
      const res = await api.post('/sales/bulk-vat-update', payload);
      toast.success(`Updated: ${res.data.updated}, Skipped (VAT exists): ${res.data.skipped}`);
      setBulkVatMode(false); setSelectedSaleIds(new Set()); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk VAT failed');
    }
  };

  const handleReceiveCredit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/sales/${receivingSale.id}/receive-credit`, {
        payment_mode: receivePayment.payment_mode,
        amount: parseFloat(receivePayment.amount),
        received_by_branch_id: receivePayment.received_by_branch_id || receivingSale?.branch_id || null,
      });
      toast.success('Credit payment received');
      setShowReceiveDialog(false);
      setReceivePayment({ payment_mode: 'cash', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive payment');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
            <div>
              <div className="h-8 w-48 bg-muted rounded-lg animate-pulse" />
              <div className="h-4 w-64 bg-muted rounded mt-2 animate-pulse" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-3 rounded-xl border bg-muted/50 animate-pulse">
                <div className="h-3 w-16 bg-muted rounded mb-2" />
                <div className="h-6 w-24 bg-muted rounded" />
              </div>
            ))}
          </div>
          <div className="h-64 bg-muted/50 rounded-xl border animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  const totals = calculateTotals();

  return (
    <DashboardLayout>
      <SaleJournalDialog saleId={journalSaleId} onClose={() => setJournalSaleId(null)} />
      <div className="space-y-6">
        {showHeader && (<>
        <FinancialSummaryHeader branchId={branchSel.join(',')} />
        <SalesVsBankWidget
          startDate={searchFilters?.date?.start || dateRange?.start}
          endDate={searchFilters?.date?.end || dateRange?.end}
        />

        <BranchSummaryCard
          branchMonthlySales={branchMonthlySales}
          amountView={amountView}
          branchView={branchView}
          onAmountViewChange={handleAmountViewChange}
          onBranchViewChange={handleBranchViewChange}
          branches={branches}
        />
        </>)}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <ReportHeader icon={ShoppingCart} title={<span data-testid="sales-page-title">Sales Management</span>} subtitle="Track sales with flexible payment options" testId="sales-header" />
          <div className="flex gap-2 items-center flex-wrap">
            <PageTour storageKey="tour_sales" steps={[
              { target: '[data-testid="add-sale-button"]', title: 'Add Sale', content: 'Record new sales transactions with flexible payment options (cash, bank, credit).' },
              { target: '[data-testid="branch-sale-tab"], [data-testid="online-sale-tab"]', title: 'Sale Types', content: 'Switch between branch sales and online platform sales with commission tracking.' },
              { target: '[data-testid="sales-summary-bar"]', title: 'Summary', content: 'Quick overview of total sales by payment method for the selected period.' },
            ]} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl h-9 text-xs" data-testid="sales-view-menu">
                  <SlidersHorizontal size={14} className="mr-1" /> View <ChevronDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="sales-view-menu-content">
                <DropdownMenuLabel className="text-xs">Customize this page</DropdownMenuLabel>
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleHeader(); }} data-testid="sales-toggle-header-btn">
                  {showHeader ? <ChevronUp size={14} className="mr-2" /> : <ChevronDown size={14} className="mr-2" />}
                  {showHeader ? 'Hide summary panel' : 'Show summary panel'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleZatca(); }} data-testid="sales-toggle-zatca-btn">
                  {showZatca ? <ChevronUp size={14} className="mr-2" /> : <ChevronDown size={14} className="mr-2" />}
                  {showZatca ? 'Hide ZATCA filter' : 'Show ZATCA filter'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl h-9 text-xs" data-testid="sales-export-menu">
                  <Download size={14} className="mr-1" /> Export <ChevronDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-2 w-64" data-testid="sales-export-menu-content">
                <DropdownMenuLabel className="text-xs">Export sales</DropdownMenuLabel>
                <label
                  className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none rounded-lg px-1 py-1.5 hover:text-foreground transition-colors"
                  data-testid="sales-export-pivot-toggle"
                  title="Include a second sheet with Branch × Payment-Mode totals"
                >
                  <input
                    type="checkbox"
                    checked={includePivot}
                    onChange={(e) => setIncludePivot(e.target.checked)}
                    className="accent-primary h-3 w-3"
                    data-testid="sales-export-pivot-checkbox"
                  />
                  + Pivot sheet
                </label>
                <div className="pt-1 flex flex-wrap gap-1">
                  <ExportButtons branchIds={branchSel}
                    dataType="sales"
                    startDate={searchFilters?.date?.start || dateRange?.start}
                    endDate={searchFilters?.date?.end || dateRange?.end}
                    filters={{
                      branch_id: searchFilters?.branch_id || null,
                      payment_method: searchFilters?.payment_mode || null,
                      include_pivot: includePivot || null,
                    }}
                  />
                  <PDFExportButton reportType="sales" label="Branded PDF" />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl h-9 text-xs" data-testid="sales-more-actions">
                  <MoreHorizontal size={14} className="mr-1" /> More Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  disabled={mergingPdf || pdfSelectedIds.length === 0}
                  onSelect={handleMergeSelectedPdf}
                  data-testid="merge-selected-pdf-btn"
                >
                  <FileDown size={14} className="mr-2 text-info" />{mergingPdf ? 'Merging…' : `Merge Selected to PDF (${pdfSelectedIds.length})`}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="sales-help-btn"
                  onSelect={() => { Object.keys(localStorage).filter(k => k.includes('sales_tour')).forEach(k => localStorage.removeItem(k)); localStorage.setItem('ssc_sales_tour_enabled', 'true'); window.location.reload(); }}
                >
                  <HelpCircle size={14} className="mr-2" /> Tour
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {canReconcile && (
            <Button
              onClick={() => setShowReconcile(true)}
              data-testid="sales-reconciliation-button"
              variant="outline"
              className="rounded-xl border-primary/40 text-primary hover:bg-primary/10"
              size="sm"
            >
              <Scale size={16} className="mr-1" />
              Sales Reconciliation
            </Button>
            )}
            <Button
              onClick={() => setShowForm(!showForm)}
              data-testid="add-sale-button-2"
              className="rounded-xl bg-primary hover:bg-primary/90"
              size="sm"
            >
              <Plus size={16} className="mr-1" />
              Add Sale
            </Button>
          </div>
        </div>

        {showForm && (
          <SaleFormCard
            formData={formData}
            setFormData={setFormData}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            branches={branches}
            customers={customers}
            platforms={platforms}
            totals={totals}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); resetForm(); }}
            addPaymentRow={addPaymentRow}
            removePaymentRow={removePaymentRow}
            updatePaymentRow={updatePaymentRow}
          />
        )}

        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <CardTitle className="font-outfit">All Sales</CardTitle>
                <CheckExportBtn
                  disabled={!sales.length}
                  testId="check-export-sales"
                  title={groupByName ? 'Export the on-screen name groups' : 'Validate sales rows, then export CSV'}
                  onClick={() => groupByName
                    ? handleExport(
                        salesByName.map((r) => ({ counterparty: r.name, date: '', amount: r.total, status: `${r.count} order(s)` })),
                        'sales_by_name.csv', REPORT_EXPORT_COLS, 'name groups',
                      )
                    : handleExport(
                        sales.map((s) => ({ counterparty: s.customer_name || s.platform_name || 'Walk-in', date: s.date, amount: s.total_amount, status: s.payment_status || s.payment_method || '' })),
                        'sales_report.csv', REPORT_EXPORT_COLS, 'sales records',
                      )}
                />
              </div>
            </div>
            {(groupByName || dailyView) && totalRecords > sales.length && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="sales-view-truncated">
                This view groups the first {sales.length.toLocaleString()} of {totalRecords.toLocaleString()} sales. Totals above are complete; narrow the date range for a complete grouping.
              </div>
            )}
            {/* H4.2C — single consolidated filter bar (relocated from the page header to remove the duplicate branch/date filters). */}
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="sales-filter-bar">
              <BranchMultiSelect value={branchSel} onChange={setBranchSel} branches={branches} testid="sales-branch-multiselect" />
              {(() => {
                const salesView = groupByName ? 'name' : dailyView ? 'daily' : 'flat';
                const setSalesView = (v) => { setGroupByName(v === 'name'); setDailyView(v === 'daily'); };
                const seg = [
                  { v: 'flat', label: 'Flat', testid: 'sales-view-flat', title: 'List each transaction on its own row' },
                  { v: 'daily', label: 'Daily', testid: 'sales-daily-view-toggle', title: 'Group sales by day (collapsible)' },
                  { v: 'name', label: 'Name', testid: 'sales-group-by-name-toggle', title: 'Group sales by customer / counterparty name' },
                ];
                return (
                  <div className="inline-flex items-center rounded-xl border border-border bg-muted/40 p-0.5 h-9" role="group" data-testid="sales-view-segmented">
                    {seg.map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setSalesView(s.v)}
                        data-testid={s.testid}
                        title={s.title}
                        aria-pressed={salesView === s.v}
                        className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${salesView === s.v ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
              <select value={statusSel} onChange={(e) => setStatusSel(e.target.value)} className="h-9 rounded-lg border border-border bg-background text-sm px-2" data-testid="sales-status-filter">
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="received">Received</option>
                <option value="pending">Pending</option>
                <option value="partial">Partial</option>
              </select>
              {showZatca && (
                <select value={zatcaSel} onChange={(e) => setZatcaSel(e.target.value)} className="h-9 rounded-lg border border-border bg-background text-sm px-2" data-testid="sales-zatca-filter" title="Filter by ZATCA reporting status">
                  <option value="">ZATCA: All</option>
                  <option value="gaps">Not reported / Failed</option>
                  <option value="not_reported">Not reported</option>
                  <option value="failed">Failed</option>
                  <option value="reported">Reported</option>
                </select>
              )}
            </div>
            <DateQuickFilter onFilterChange={(range) => setDateRange(range)} className="mt-2" />
          </CardHeader>
          <CardContent>
            {urlDateFilter && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-orange-50 rounded-lg border border-orange-200" data-testid="date-filter-banner">
                <CalendarDays size={14} className="text-orange-600" />
                <span className="text-xs font-medium text-orange-700">Filtered by date: {urlDateFilter}{urlBranch ? ` · branch: ${branches.find((b) => b.id === urlBranch)?.name || urlBranch}` : ''}</span>
                <Button
                  size="sm" variant="ghost" className="h-6 px-2 text-xs text-orange-600 hover:text-orange-800 ml-auto"
                  onClick={() => window.location.href = '/sales'} data-testid="clear-date-filter"
                >
                  Clear filter
                </Button>
              </div>
            )}

            <AdvancedSearch
              onSearch={(f) => {
                if (urlDateFilter && !f.date) {
                  const d = searchParams.get('date');
                  setSearchFilters({ ...f, date: { start: d || urlStart, end: d || urlEnd }, ...(urlBranch && !f.branch_id ? { branch_id: urlBranch } : {}) });
                } else {
                  setSearchFilters(f);
                }
              }}
              config={{
                searchFields: ['notes'],
                placeholder: 'Search sales by notes...',
                filters: [
                  {
                    key: 'payment_mode',
                    label: 'Payment Mode',
                    type: 'select',
                    options: [
                      { value: 'cash', label: 'Cash' },
                      { value: 'bank', label: 'Bank' },
                      { value: 'credit', label: 'Credit' },
                      { value: 'online_platform', label: 'Online' },
                    ],
                  },
                  { key: 'final_amount', label: 'Amount', type: 'range' },
                ],
              }}
              className="mb-4"
            />

            <BulkVatToolbar
              bulkVatMode={bulkVatMode}
              setBulkVatMode={setBulkVatMode}
              selectedSaleIds={selectedSaleIds}
              setSelectedSaleIds={setSelectedSaleIds}
              sales={sales}
              bulkVatRate={bulkVatRate}
              setBulkVatRate={setBulkVatRate}
              bulkVatInclusive={bulkVatInclusive}
              setBulkVatInclusive={setBulkVatInclusive}
              bulkSkipExisting={bulkSkipExisting}
              setBulkSkipExisting={setBulkSkipExisting}
              bulkFilterBranch={bulkFilterBranch}
              setBulkFilterBranch={setBulkFilterBranch}
              bulkFilterStart={bulkFilterStart}
              setBulkFilterStart={setBulkFilterStart}
              bulkFilterEnd={bulkFilterEnd}
              setBulkFilterEnd={setBulkFilterEnd}
              branches={branches}
              onSelectAll={selectAllSales}
              onApply={applyBulkVatSales}
            />

            <SalesGrandTotalBar
              sales={sales}
              searchFilters={searchFilters}
              amountView={amountView}
              serverTotals={{
                total: serverTotalAmount,
                cash: serverCashTotal,
                bank: serverBankTotal,
                credit: serverCreditTotal,
                online: serverOnlineTotal,
                receivable: serverReceivableTotal,
                received: serverReceivedTotal,
                platformPending: serverPlatformPending,
                vat: serverVat,
                count: totalRecords,
              }}
            />

            {groupByName ? (
              <div className="overflow-x-auto border rounded-xl mt-2" data-testid="sales-by-name-table">
                <table className="w-full">
                  <thead className="bg-muted/40">
                    <tr className="border-b">
                      <th className="text-left p-2 text-xs font-medium">Customer / Counterparty</th>
                      <th className="text-right p-2 text-xs font-medium">Orders</th>
                      <th className="text-right p-2 text-xs font-medium">Total ({amountView})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedNameGroups.length === 0 ? (
                      <tr><td colSpan={3} className="p-0">
                        <FilterEmptyNudge show onReset={() => { setDateRange({ start: localToday(), end: localToday() }); setBranchSel([]); setStatusSel(''); setSearchFilters({}); }} label="No sales for the current filter" />
                      </td></tr>
                    ) : pagedNameGroups.map((r) => (
                      <tr key={r.name} className="border-b hover:bg-muted/30" data-testid={`sales-name-row-${r.name}`}>
                        <td className="p-2 text-xs font-medium">{r.name}</td>
                        <td className="p-2 text-xs text-right">{r.count}</td>
                        <td className="p-2 text-xs text-right font-bold">{cur()} {Number(r.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-muted-foreground p-2">Showing {pagedNameGroups.length} of {salesByName.length} name group(s) · page size applies to groups.</p>
              </div>
            ) : dailyView ? (
            <SalesDailyTable
              sales={pagedSales}
              branches={branches}
              customers={customers}
              searchFilters={searchFilters}
              expandedSalesDates={expandedSalesDates}
              setExpandedSalesDates={setExpandedSalesDates}
              posBranchExpanded={posBranchExpanded}
              setPosBranchExpanded={setPosBranchExpanded}
              onExportDayCsv={exportDayCsv}
              onInlineUpdateSale={inlineUpdateSale}
              onDeleteSale={handleDelete}
              onViewJournals={setJournalSaleId}
              onOpenReceiveCredit={(sale) => { setReceivingSale(sale); setShowReceiveDialog(true); }}
              invoiceBySale={invoiceBySale}
              pdfSelectedIds={pdfSelectedIds}
              onTogglePdfSelect={togglePdfSelect}
              onEmailInvoice={handleEmailInvoiceForSale}
              emailingSaleId={emailingSaleId}
            />
            ) : (
            pagedSales.length === 0 ? (
              <FilterEmptyNudge show onReset={() => { setDateRange({ start: localToday(), end: localToday() }); setBranchSel([]); setStatusSel(''); setSearchFilters({}); }} label="No sales for the current filter" />
            ) : (
            <SalesFlatTable
              sales={zatcaSel ? pagedSales.filter((s) => {
                const z = (s.zatca_status || '').toUpperCase();
                if (zatcaSel === 'not_reported') return !z;
                if (zatcaSel === 'failed') return z === 'FAILED';
                if (zatcaSel === 'gaps') return !z || z === 'FAILED';
                if (zatcaSel === 'reported') return z === 'REPORTED' || z === 'CLEARED';
                return true;
              }) : pagedSales}
              branches={branches}
              searchFilters={searchFilters}
              onDeleteSale={handleDelete}
              onViewJournals={setJournalSaleId}
              onOpenReceiveCredit={(sale) => { setReceivingSale(sale); setShowReceiveDialog(true); }}
            />
            ))}

            {totalRecords > 0 && (
              <div className="flex items-center justify-between mt-4 px-2 gap-2 flex-wrap" data-testid="sales-pagination">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {groupByName
                      ? `${salesByName.length.toLocaleString()} name group(s) · ${totalRecords.toLocaleString()} records`
                      : `${totalRecords.toLocaleString()} records`}
                  </span>
                  <PageSizeSelector value={pageSize} onChange={(n) => setPageSize(n)} label={groupByName ? 'Groups / page' : 'Rows / page'} testid="sales-page-size" />
                </div>
                {effectiveTotalPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    size="sm" variant="outline" disabled={currentPage <= 1} className="h-8 w-8 p-0"
                    onClick={() => goToPage(currentPage - 1)} data-testid="sales-prev-page"
                  ><ChevronLeft size={14} /></Button>
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let end = Math.min(effectiveTotalPages, start + maxVisible - 1);
                    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                    if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < effectiveTotalPages) { if (end < effectiveTotalPages - 1) pages.push('...'); pages.push(effectiveTotalPages); }
                    return pages.map((p, idx) => p === '...' ? (
                      <span key={`dots-${idx}`} className="text-xs text-muted-foreground px-1">...</span>
                    ) : (
                      <Button
                        key={p} size="sm" variant={p === currentPage ? 'default' : 'outline'} className="h-8 w-8 p-0 text-xs"
                        onClick={() => goToPage(p)} data-testid={`sales-page-${p}`}
                      >{p}</Button>
                    ));
                  })()}
                  <Button
                    size="sm" variant="outline" disabled={currentPage >= effectiveTotalPages} className="h-8 w-8 p-0"
                    onClick={() => goToPage(currentPage + 1)} data-testid="sales-next-page"
                  ><ChevronRight size={14} /></Button>
                </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <ReceiveCreditDialog
          open={showReceiveDialog}
          onOpenChange={setShowReceiveDialog}
          receivingSale={receivingSale}
          receivePayment={receivePayment}
          setReceivePayment={setReceivePayment}
          branches={branches}
          onSubmit={handleReceiveCredit}
        />

        <DuplicateWarningDialog
          open={showDuplicateWarning}
          onOpenChange={setShowDuplicateWarning}
          duplicateCount={duplicateCount}
          duplicateExisting={duplicateExisting}
          formData={formData}
          amountTotal={calculateTotals().total}
          onConfirm={() => { setShowDuplicateWarning(false); submitSale(); }}
        />

        <SalesReconciliationDialog
          open={showReconcile}
          onOpenChange={setShowReconcile}
          branches={branches}
          onPosted={() => fetchData(currentPage)}
        />
      </div>
    </DashboardLayout>
  );
}
